import { useMemo } from 'react';

import { type HomeProductCategory } from '../useHomeContent';
import { useHomeMenu } from '../useHomeMenu';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Removes products from the filterable and generic categories that are not in the products array.
 * If there are no products, returns only the generic categories.
 */
export const useHomeFiltering = (params: UseHomeFilteringParams): UseHomeFilteringResult => {
  const { products = [], filterableCategories, genericCategories } = params;

  return useMemo(() => {
    if (products.length === 0) {
      return {
        filterableCategories: [],
        genericCategories,
      };
    }

    const menuProductsBySlug = getMenuProductsBySlug(products);

    return {
      filterableCategories: filterCategories(filterableCategories, menuProductsBySlug),
      genericCategories: filterCategories(genericCategories, menuProductsBySlug),
    };
  }, [products, filterableCategories, genericCategories]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Removes categories that have no products.
 */
function filterCategories(
  categories: HomeProductCategory[],
  menuProductsBySlug: MenuProductsBySlug,
) {
  return categories
    .map((category) => filterCategoryProducts(category, menuProductsBySlug))
    .filter((category) => category.products.length > 0);
}

/**
 * Removes products that are not in the selected menu and enriches them with the menu data.
 */
function filterCategoryProducts(
  category: HomeProductCategory,
  menuProductsBySlug: MenuProductsBySlug,
) {
  return {
    ...category,
    products: category.products
      .filter((product) => menuProductsBySlug.has(product.baseSlug))
      .map((product) => ({
        ...product,
        id: menuProductsBySlug.get(product.baseSlug)!.id,
        slug: menuProductsBySlug.get(product.baseSlug)!.slug,
        isModifiable: menuProductsBySlug.get(product.baseSlug)!.isModifiable,
        cost: menuProductsBySlug.get(product.baseSlug)!.cost,
        allergenProperties: menuProductsBySlug.get(product.baseSlug)!.allergenProperties,
        caloriesFromFat: menuProductsBySlug.get(product.baseSlug)!.caloriesFromFat ?? 0,
        totalFatG: menuProductsBySlug.get(product.baseSlug)!.totalFatG ?? 0,
        totalCarbsG: menuProductsBySlug.get(product.baseSlug)!.totalCarbsG ?? 0,
        dietaryFiberG: menuProductsBySlug.get(product.baseSlug)!.dietaryFiberG ?? 0,
        sugarsG: menuProductsBySlug.get(product.baseSlug)!.sugarsG ?? 0,
        proteinG: menuProductsBySlug.get(product.baseSlug)!.proteinG ?? 0,
        co2eKg: menuProductsBySlug.get(product.baseSlug)!.co2eKg ?? 0,
      })),
  };
}

/**
 * Map of products by base slug, to be provided in the Contentful content.
 * To be replaced with the generic slug once available.
 */
function getMenuProductsBySlug(products: NonNullable<UseHomeFilteringParams['products']>) {
  return new Map(
    products
      .filter((product): product is MenuProductWithSlug => Boolean(product.baseSlug))
      .map((product) => [product.baseSlug, product]),
  );
}

// ─── Types ───────────────────────────────────────────────────────────────────

type MenuProduct = NonNullable<ReturnType<typeof useHomeMenu>['products']>[number];

type MenuProductWithSlug = MenuProduct & { slug: string; baseSlug: string };

type MenuProductsBySlug = Map<string, MenuProductWithSlug>;

type UseHomeFilteringParams = {
  products: ReturnType<typeof useHomeMenu>['products'];
  filterableCategories: HomeProductCategory[];
  genericCategories: HomeProductCategory[];
};

type UseHomeFilteringResult = {
  filterableCategories: HomeProductCategory[];
  genericCategories: HomeProductCategory[];
};
